import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { Notification } from '../types/organization';
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

interface NotificationContextType {
  notifications: Notification[];
  markAsRead: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { organization } = useOrganization();
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchNotifications = useCallback(async () => {
    if (!organization?.id) {
      setNotifications([]);
      return;
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('org_id', organization.id)
      .order('created_at', { ascending: false });

    if (error) {
      logger.error('Error fetching notifications:', error);
      setNotifications([]);
      return;
    }

    setNotifications(
      (data || [])
        // Org-wide rows (recipient_id null) plus this user's personal rows.
        // RLS already enforces this; the client filter guards realtime refetches.
        .filter(row => !row.recipient_id || row.recipient_id === currentUser?.uid)
        .map(row => ({
          id: row.id,
          type: row.type,
          aircraftId: row.aircraft_id || '',
          aircraftName: row.aircraft_name || '',
          message: row.message || '',
          createdAt: row.created_at || new Date().toISOString(),
          readBy: row.read_by || [],
          severity: row.severity || 'info',
        }))
    );
  }, [organization?.id, currentUser?.uid]);

  useEffect(() => {
    fetchNotifications();

    if (!organization?.id) {
      return;
    }

    const channel = supabase
      .channel(`notifications:${organization.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `org_id=eq.${organization.id}`,
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organization?.id, fetchNotifications]);

  const markAsRead = async (id: string) => {
    if (!organization?.id || !currentUser) return;
    try {
      const existing = notifications.find(n => n.id === id)?.readBy || [];
      const updatedReadBy = Array.from(new Set([...existing, currentUser.uid]));

      const { error } = await supabase
        .from('notifications')
        .update({ read_by: updatedReadBy })
        .eq('id', id);

      if (error) {
        throw error;
      }

      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, readBy: updatedReadBy } : n))
      );
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, markAsRead }}>
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
} 